import React from "react";
import styled from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import Drawer from "../../common/Drawer/AgentListDrawerModal";
import { Heading } from "../../../styles/component/style";
import CustomTable from "../../common/CustomTable/CustomTable";
import { makeStyles, Tooltip } from "@material-ui/core";
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import AccordionDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import _ from "lodash";
import CaseService from "../../../services/CaseService";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import useUser from "../../../hooks/useUser";

const Index = ({
  opendrawer,
  setOpenDrawer,
  MTRef,
  partyList,
  displayAuthUrl,
  caseId,
  setTriggerCaseCall,
  agentRole,
  partyId,
  partyRole,
}) => {
  return (
    <Drawer state={opendrawer} setState={setOpenDrawer} label="Drawer">
      <Table
        {...{
          setOpenDrawer,
          MTRef,
          displayAuthUrl,
          caseId,
          setTriggerCaseCall,
          agentRole,
          partyId,
          partyRole,
        }}
        data={partyList}
      />
    </Drawer>
  );
};

export default Index;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: "#f9f9f9",
    border: "solid 0.5px #e1e3ee",
    boxShadow: "none",
    margin: "16px 0px 0px !important",
    "& .MuiIconButton-root": {
      padding: 0,
      marginRight: 15,
      backgroundColor: "#fff",
    },
    "& .MuiSvgIcon-root": {
      fill: "#00838c",
    },
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

const Table = ({
  setOpenDrawer,
  MTRef,
  displayAuthUrl,
  data,
  caseId,
  setTriggerCaseCall,
  agentRole,
  partyId,
  partyRole,
}) => {
  return (
    <div>
      <Header>
        <Heading style={{ marginBottom: "unset" }}>Counsel Details</Heading>
        <ImageWrapper>
          <img
            src={require("../../../assets/images/closeModal.svg")}
            onClick={() => setOpenDrawer(false)}
            alt="close"
          />
        </ImageWrapper>
      </Header>
      <AccordionContainer>
        {data.map((item, index) => (
          <TableWithAccordion
            key={index}
            title={
              item?.partyRole
                ? `${item?.party?.name}'s Counsel details (${_.capitalize(
                    item?.partyRole
                  )})`
                : `${item?.party?.name}'s Counsel details`
            }
            {...{
              MTRef,
              item,
              displayAuthUrl,
              caseId,
              setTriggerCaseCall,
              agentRole,
              partyId,
              partyRole,
            }}
          />
        ))}
      </AccordionContainer>
    </div>
  );
};

function TableWithAccordion({
  MTRef,
  title,
  item,
  caseId,
  setTriggerCaseCall,
  agentRole,
  partyId,
  partyRole,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const { userObject } = useUser();

  const removeAgentsFromCases = async (agentId, caseId) => {
    try {
      setLoader({ state: true, message: "remove agent..." });
      const response = await CaseService.removeAgentsFromCases(agentId, caseId);
      if (response) {
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      }
      setTriggerCaseCall(true);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const columns = [
    {
      field: "user.name",
      title: "Counsel Name",
      sorting: false,
      headerStyle: { textAlign: "left", width: "25%" },
      cellStyle: { padding: "12px 16px", textAlign: "left" },
      render: (rowData) => (
        <AgentNameContainer>
          <span>{rowData?.user?.name}</span>
          {rowData?.role === "primary" && (
            <Tooltip title="Primary Counsel" placement="top">
              <PrimaryBadge>℗</PrimaryBadge>
            </Tooltip>
          )}
        </AgentNameContainer>
      ),
    },
    {
      field: "email",
      title: "Email",
      sorting: false,
      headerStyle: { textAlign: "left", width: "35%" },
      cellStyle: { padding: "12px 16px", textAlign: "left" },
      render: (rowData) => (
        <EmailLink href={`mailto:${rowData.email}`}>{rowData.email}</EmailLink>
      ),
    },
    {
      field: "user.mobile",
      title: "Phone",
      sorting: false,
      headerStyle: { textAlign: "left", width: "20%" },
      cellStyle: { padding: "12px 16px", textAlign: "left" },
      render: (rowData) => (
        <PhoneText>{rowData.user?.mobile || "N/A"}</PhoneText>
      ),
    },
    {
      field: "actions",
      title: "Action",
      sorting: false,
      hidden: item?.partyRole === agentRole ? false : true,
      headerStyle: { textAlign: "center", width: "20%" },
      cellStyle: { padding: "12px 16px", textAlign: "center" },
      render: (rowData) => {
        const isCurrentUserPrimary = partyRole?.ownerId === rowData?.userId;
        const isLoggedInUserPrimary = partyRole?.ownerId === userObject?.id;
        const canRemove = isLoggedInUserPrimary && !isCurrentUserPrimary;

        return canRemove ? (
          <DeleteButtonContainer
            onClick={() => removeAgentsFromCases(rowData.id, caseId)}
            title="Remove Agent"
          >
            <DeleteIcon>✕</DeleteIcon>
          </DeleteButtonContainer>
        ) : (
          <DisabledAction>—</DisabledAction>
        );
      },
    },
  ];

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <AccordionHeading style={{ marginLeft: 24 }}>{title}</AccordionHeading>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          {item?.party?.agents?.length ? (
            <CustomTable
              pluralTitle="Agents"
              singularTitle="Agent"
              {...{
                columns,
                MTRef,
              }}
              state={{
                data: item?.party?.agents,
              }}
              data={item?.party?.agents}
              noToolbar
              pageSize={1000}
              hidePagination={true}
            />
          ) : (
            <div className="no-result">No Agents Found</div>
          )}
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 40px;
  margin-top: 15px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
`;
export const TableContainer = styled.div`
  /* margin: 30px 29px auto 38px; */
  width: 100%;
  & .no-result {
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 16px;
    color: ${COLORS.COLOR_DARK};
    text-align: center;
  }
`;
export const AccordionHeading = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
`;

export const AccordionContainer = styled.div`
  padding: 0 20px;
`;

export const ImageWrapper = styled.div`
  cursor: pointer;
`;
export const HyperLink = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.BTN_GREEN};
  text-decoration: underline;
  margin-right: ${({ marginRight }) => (marginRight ? marginRight : "0px")};
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const StyledTitle = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  width: 200px;
`;

export const Container = styled.div`
  display: flex;
`;

export const RoleContainer = styled.p`
  color: ${COLORS.COLOR_DARK};
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontBold};
  margin-left: 3px;
`;

const AgentNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 14px;
  font-weight: 500;

  & span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const PrimaryBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #e8f4f8;
  border-radius: 50%;
  color: #00838c;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
  cursor: help;
`;

const EmailLink = styled.a`
  color: #00838c;
  text-decoration: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }

  &:visited {
    color: #00838c;
  }
`;

const PhoneText = styled.p`
  margin: 0;
  font-family: ${theme.fonts.primaryFont};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
`;

const DeleteButtonContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #ffe8e8;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:hover {
    background-color: ${COLORS.LOGOUT_RED};
    transform: scale(1.05);

    & div {
      color: white;
    }
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DeleteIcon = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${COLORS.LOGOUT_RED};
  transition: color 0.3s ease;
`;

const DisabledAction = styled.div`
  color: #bdc3c7;
  font-size: 18px;
  font-weight: bold;
`;

// Optional: Table wrapper styling if using Material-Table
export const TableStyles = `
  .MuiPaper-root {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 8px !important;
  }

  .MuiTable-root {
    background-color: white;
  }

  thead tr {
    background-color: #f8f9fa !important;
    border-bottom: 2px solid #e1e3ee !important;
  }

  thead th {
    font-family: openSans-SemiBold, sans-serif !important;
    color: #7fa1c3 !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  tbody tr {
    border-bottom: 1px solid #e1e3ee !important;
    transition: background-color 0.2s ease;
  }

  tbody tr:hover {
    background-color: #f8fafc !important;
  }

  tbody tr:last-child {
    border-bottom: none !important;
  }

  tbody td {
    font-family: openSans, sans-serif !important;
  }
`;
